import React from "react";
import { Badge } from "@/components/Badge/Badge";
import { OpportunityEvent } from "../../api/types";

interface OpportunityEventTimelineProps {
  events: OpportunityEvent[];
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getDotColor(operationType: string): string {
  const op = operationType.toUpperCase();
  if (op.includes("COMPLETED") || op.includes("SIGNED"))
    return "bg-emerald-400 dark:bg-emerald-500";
  if (
    op.includes("RECALLED") ||
    op.includes("DECLINED") ||
    op.includes("CANCELLED")
  )
    return "bg-red-400 dark:bg-red-500";
  if (op.includes("VIEWED")) return "bg-blue-400 dark:bg-blue-500";
  if (op.includes("SENT") || op.includes("REMINDER"))
    return "bg-amber-400 dark:bg-amber-500";
  return "bg-gray-300 dark:bg-slate-600";
}

function buildActorEventCounts(
  events: OpportunityEvent[],
): Map<number, number> {
  const counts = new Map<number, number>();
  for (const event of events) {
    if (event.actor?.actorId != null) {
      counts.set(
        event.actor.actorId,
        (counts.get(event.actor.actorId) ?? 0) + 1,
      );
    }
  }
  return counts;
}

function OpportunityEventTimeline({ events }: OpportunityEventTimelineProps) {
  if (events.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No events recorded.
      </p>
    );
  }

  const actorEventCounts = buildActorEventCounts(events);

  return (
    <ol className="relative space-y-0 border-l border-gray-200 dark:border-slate-700 ml-2">
      {events.map((event) => (
        <li
          className="relative pl-6 pb-6 last:pb-0"
          key={event.opportunityEventId}
        >
          {/* Timeline dot — color indicates event category */}
          <span
            className={`absolute left-0 top-1.5 -translate-x-1/2 h-2 w-2 rounded-full ring-2 ring-white dark:ring-slate-950 ${getDotColor(event.operationType)}`}
          />

          {/* Timestamp */}
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {formatDate(event.createdAt)}
          </p>

          {/* Operation type badge + stage */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge variant="grey">{event.operationType}</Badge>
            {event.stage && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {event.stage}
              </span>
            )}
          </div>

          {/* Subject */}
          {event.subject && (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              {event.subject}
            </p>
          )}

          {/* Actor email + multi-event indicator */}
          <p className="text-sm text-gray-900 dark:text-white font-normal flex items-center gap-1.5">
            {event.actorEmail}
            {event.actor?.actorId != null &&
              (actorEventCounts.get(event.actor.actorId) ?? 0) > 1 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({actorEventCounts.get(event.actor.actorId)} events)
                </span>
              )}
          </p>

          {/* Device metadata */}
          {(event.actorUserAgent ||
            event.actorOs ||
            event.actorRegion ||
            event.actorIpAddress) && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {[
                event.actorOs,
                event.actorRegion,
                event.actorIpAddress,
                event.actorUserAgent,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
}

export default OpportunityEventTimeline;
